import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Upload, Trash2, Download, Search, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { knowledgeService } from '@/services/knowledgeService';
import { SlidePanel } from '@/components/ui/slide-panel';

interface AppFAQManagerProps {
  onUpdate?: () => void;
}

interface FAQDocument {
  id: string;
  title: string;
  file_name: string;
  file_type: string;
  file_size?: number;
  created_at: string;
  uploaded_by: string;
  metadata?: Record<string, any>;
  is_primary?: boolean;
}

export const AppFAQManager: React.FC<AppFAQManagerProps> = ({ onUpdate }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<FAQDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<FAQDocument | null>(null);
  
  // Use Solerro tenant ID for shared FAQ documents
  const SHARED_TENANT_ID = '73ef7154-be00-4600-a4a6-34185ea3e6b4';

  useEffect(() => {
    let mounted = true;
    
    const load = async () => {
      if (user && mounted) {
        await loadDocuments();
      }
    };
    
    load();
    
    return () => {
      mounted = false;
    };
  }, [user]);

  const loadDocuments = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    // Prevent concurrent loading
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      // Load FAQ documents from the shared tenant
      const docs = await knowledgeService.getDocuments(SHARED_TENANT_ID, 'app_faq');
      setDocuments(docs.map(doc => ({
        ...doc,
        is_primary: doc.metadata?.is_primary || false
      })));
    } catch (error) {
      console.error('Error loading FAQ documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const validateFile = (file: File): boolean => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain'
    ];

    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: `${file.name} exceeds 10MB limit`,
        variant: "destructive"
      });
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: `${file.name} must be PDF, Word, or Text file`,
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleFileUpload = async (files: FileList) => {
    
    // For FAQ documents, we don't need tenant_id since they're shared
    if (!user?.id) {
      console.error('No user ID found');
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (!validateFile(file)) continue;

      try {
        // Check if this is a primary FAQ document (based on name)
        const isPrimary = file.name.toLowerCase().includes('primary') || 
                         file.name.toLowerCase().includes('main') ||
                         file.name.toLowerCase().includes('standard');

        const metadata = {
          is_primary: isPrimary,
          category: 'company_faq',
          access_level: 'all_tenants' // FAQs are shared across all tenants
        };

        const result = await knowledgeService.uploadDocument(
          file,
          file.name.replace(/\.[^/.]+$/, ""), // Remove extension for title
          'app_faq',
          SHARED_TENANT_ID, // Use Solerro tenant for shared FAQs
          user.id,
          metadata
        );

        if (result.success) {
          results.push(`✅ ${file.name}${isPrimary ? ' (Primary FAQ)' : ''}`);
        } else {
          results.push(`❌ ${file.name}: ${result.error}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        results.push(`❌ ${file.name}: ${(error as Error).message || 'Upload failed'}`);
      }
    }

    setUploading(false);
    
    if (results.length > 0) {
      toast({
        title: "Upload Complete",
        description: results.join('\n'),
        duration: 5000
      });
    } else {
      toast({
        title: "No files uploaded",
        description: "Please check the console for errors",
        variant: "destructive"
      });
    }

    loadDocuments();
    onUpdate?.();
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [user]);

  const handleDeleteDocument = async (doc: FAQDocument) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete || !user?.id) return;

    try {
      const success = await knowledgeService.deleteDocument(documentToDelete.id);
      
      if (success) {
        toast({
          title: "Document deleted",
          description: `${documentToDelete.title} has been removed`
        });
        loadDocuments();
        onUpdate?.();
      } else {
        throw new Error('Failed to delete document');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };


  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.file_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            App FAQ Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info Box */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Company FAQ Knowledge Base</h3>
            <p className="text-blue-700 text-sm mb-3">
              Upload FAQ documents that will be shared across all tenants. These documents provide 
              quick answers about your company, services, and common questions.
            </p>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• Primary FAQ documents are prioritized in search results</li>
              <li>• Supports PDF, Word, and Text files up to 10MB</li>
              <li>• Documents are automatically indexed for AI search</li>
              <li>• Shared across all organizations (not tenant-specific)</li>
              <li>• <a href="https://docs.google.com/document/d/1wGpyhNsJH_8ygCax4oHFaaYG39hZWflpDbaRapel9u4/edit?usp=sharing" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="underline hover:text-blue-800">
                     Solerro Google Doc
                  </a>
              </li>
            </ul>
          </div>

          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium mb-2">
              Drop FAQ documents here or click to browse
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              PDF, Word, or Text files (max 10MB)
            </p>
            <div>
              <input
                id="faq-file-upload"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,.md"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFileUpload(e.target.files);
                  }
                }}
                disabled={uploading}
              />
              <Button
                type="button"
                disabled={uploading}
                className="cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  const fileInput = document.getElementById('faq-file-upload') as HTMLInputElement;
                  if (fileInput) {
                    fileInput.click();
                  } else {
                    console.error('File input not found');
                  }
                }}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Select Files
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search FAQ documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Document List */}
          {filteredDocuments.length > 0 ? (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-sm text-muted-foreground">
                  {filteredDocuments.length} FAQ document{filteredDocuments.length !== 1 ? 's' : ''}
                </h3>
              </div>
              {filteredDocuments.map((doc) => (
                <Card key={doc.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <h4 className="font-medium">{doc.title}</h4>
                        {doc.is_primary && (
                          <Badge variant="default" className="text-xs">Primary FAQ</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {doc.file_name} • {formatFileSize(doc.file_size)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Uploaded {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="font-medium mb-2">No FAQ Documents</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? "No documents match your search"
                  : "Upload your first FAQ document to get started"}
              </p>
            </Card>
          )}

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Quick Response Priority</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  FAQ documents are checked first for the fastest possible responses.
                </p>
                <Badge variant="outline">Response time: &lt; 1 second</Badge>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Shared Knowledge</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  All tenants access the same FAQ knowledge for consistency.
                </p>
                <Badge variant="secondary">Cross-tenant sharing</Badge>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Panel */}
      <SlidePanel
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete FAQ Document"
        width="md"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete "{documentToDelete?.title}"? This action cannot be undone
            and will remove this FAQ from the knowledge base for all users.
          </p>

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmDelete} variant="destructive">
              Delete
            </Button>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
};